<template>
  <div>
    <DemoNotice v-if="isDemo" />

    <div class="mb-6">
      <h1 class="text-2xl font-bold text-gray-800 dark:text-gray-100">操作日志</h1>
      <p class="mt-1 text-sm text-gray-600 dark:text-gray-400">
        记录租户控制台的关键操作，便于追踪品牌配置、团队成员与教师管理变更。
      </p>
    </div>

    <div class="rounded-lg bg-white shadow dark:bg-gray-800">
      <div class="flex items-center justify-between border-b border-gray-200 p-4 dark:border-gray-700">
        <div>
          <h2 class="text-lg font-semibold text-gray-800 dark:text-gray-100">日志列表</h2>
          <p class="mt-1 text-xs text-gray-500 dark:text-gray-400">共 {{ total }} 条日志</p>
        </div>
      </div>

      <div class="overflow-x-auto">
        <table class="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead class="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">时间</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">操作人</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">动作</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">目标</th>
              <th class="px-4 py-3 text-left text-sm font-semibold text-gray-900 dark:text-gray-100">详情</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
            <tr v-for="log in logs" :key="log.id" class="hover:bg-indigo-50 dark:hover:bg-gray-700/60">
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ formatDate(log.createdAt) }}</td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ log.operatorDisplayName || '系统' }}</td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ actionLabel(log.action, log.detail) }}</td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ log.targetType }}<span v-if="log.targetId"> #{{ log.targetId }}</span></td>
              <td class="px-4 py-4 text-sm text-gray-700 dark:text-gray-300">{{ prettyDetail(log.detail) }}</td>
            </tr>
            <tr v-if="!logs.length">
              <td colspan="5" class="px-4 py-8 text-center text-sm text-gray-500 dark:text-gray-400">暂无操作日志</td>
            </tr>
          </tbody>
        </table>
      </div>

      <div class="flex items-center justify-between border-t border-gray-200 px-4 py-4 dark:border-gray-700">
        <button class="cancel-btn" type="button" :disabled="page <= 1" @click="loadLogs(page - 1)">上一页</button>
        <span class="text-sm text-gray-600 dark:text-gray-400">第 {{ page }} 页 / 共 {{ totalPages }} 页</span>
        <button class="cancel-btn" type="button" :disabled="page >= totalPages" @click="loadLogs(page + 1)">下一页</button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from "vue";

import { getAuditLogsAPI } from "@/api/tenantConsole";
import type { AuditLogItem } from "@/types/tenantConsole";
import DemoNotice from "@/components/DemoNotice.vue";
import { useAuthStore } from "@/stores/auth";

const authStore = useAuthStore();
const isDemo = computed(() => authStore.isDemo);

const logs = ref<AuditLogItem[]>([]);
const page = ref(1);
const limit = ref(20);
const total = ref(0);

const totalPages = computed(() => Math.max(1, Math.ceil(total.value / limit.value)));

const ACTION_LABELS: Record<string, string> = {
  login: "登录",
  update_settings: "更新租户设置",
  assign_teacher: "设置教师",
  revoke_teacher: "撤销教师",
  create_account: "创建账号",
  update_account_status: "更新账号状态",
  bind_user: "绑定用户",
  unbind_user: "解绑用户",
};

const formatDate = (dateTime: string) => new Date(dateTime).toLocaleString("zh-CN");

const parseDetail = (detail: string) => {
  if (!detail) {
    return null;
  }

  try {
    return JSON.parse(detail) as Record<string, unknown>;
  } catch {
    return null;
  }
};

const actionLabel = (action: string, detail: string) => {
  const parsedDetail = parseDetail(detail);
  if (action === "update_account_status" && parsedDetail?.operation === "reset_password") {
    return "重置密码";
  }
  if (action === "update_account_status" && parsedDetail?.operation === "change_own_password") {
    return "修改密码";
  }
  return ACTION_LABELS[action] || action;
};

const prettyDetail = (detail: string) => {
  if (!detail) {
    return "-";
  }

  const parsed = parseDetail(detail);
  if (parsed) {
    return Object.entries(parsed)
      .filter(([key]) => key !== "operation")
      .map(([key, value]) => `${key}: ${Array.isArray(value) ? value.join("，") : String(value)}`)
      .join("；");
  }

  return detail;
};

/**
 * 加载审计日志。
 */
const loadLogs = async (nextPage = page.value) => {
  const response = await getAuditLogsAPI({ page: nextPage, limit: limit.value });
  if (response.status !== 200 || !response.data) {
    return;
  }

  logs.value = response.data.logs;
  total.value = response.data.total;
  page.value = response.data.page;
  limit.value = response.data.limit;
};

onMounted(() => {
  loadLogs(1);
});
</script>
